import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { brandInfo, faqs } from '@/mocks/brand';

function BrandIntro() {
  return (
    <section className="w-full bg-background-50 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
          <div>
            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
              品牌介绍
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              关于金宝博
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
              {brandInfo.description}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-background-100 rounded-lg p-4 text-center">
                <div className="text-xl font-heading font-bold text-primary-500">{brandInfo.users}</div>
                <div className="text-xs text-foreground-500 mt-1">服务用户</div>
              </div>
              <div className="bg-background-100 rounded-lg p-4 text-center">
                <div className="text-xl font-heading font-bold text-primary-500">{brandInfo.satisfaction}</div>
                <div className="text-xs text-foreground-500 mt-1">用户满意度</div>
              </div>
              <div className="bg-background-100 rounded-lg p-4 text-center">
                <div className="text-xl font-heading font-bold text-primary-500">{brandInfo.awards}</div>
                <div className="text-xs text-foreground-500 mt-1">行业认可</div>
              </div>
              <div className="bg-background-100 rounded-lg p-4 text-center">
                <div className="text-xl font-heading font-bold text-primary-500">{brandInfo.coverage}</div>
                <div className="text-xs text-foreground-500 mt-1">服务覆盖</div>
              </div>
            </div>
          </div>
          <div className="h-80 md:h-96 rounded-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20luxurious%20corporate%20headquarters%20building%20with%20warm%20golden%20lighting%2C%20professional%20business%20atmosphere%2C%20clean%20minimalist%20architecture%2C%20glass%20and%20marble%20textures%2C%20high-end%20corporate%20brand%20image%2C%20warm%20sunset%20glow&width=700&height=500&seq=about-brand-2026&orientation=landscape"
              alt="金宝博品牌形象"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function MissionVision() {
  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div className="bg-background-50 rounded-lg p-8 md:p-10">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-5">
              <i className="ri-flag-line text-xl text-primary-500"></i>
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">
              我们的使命
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              {brandInfo.mission}
            </p>
          </div>
          <div className="bg-background-50 rounded-lg p-8 md:p-10">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-5">
              <i className="ri-eye-line text-xl text-accent-500"></i>
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">
              我们的愿景
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              {brandInfo.vision}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CoreValues() {
  return (
    <section className="w-full bg-background-50 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
            企业文化
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            核心价值观
          </h2>
          <p className="text-sm text-foreground-500 max-w-xl mx-auto">
            我们始终坚持这些核心价值观，为用户创造卓越体验
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {brandInfo.values.map((value, index) => (
            <div key={index} className="bg-background-100 rounded-lg p-6 md:p-8 text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                <span className="font-heading text-xl font-bold text-accent-500">
                  {index + 1}
                </span>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                {value.title}
              </h3>
              <p className="text-sm text-foreground-500 leading-relaxed">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Milestones() {
  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
            品牌历程
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            发展历程
          </h2>
          <p className="text-sm text-foreground-500 max-w-xl mx-auto">
            从创立至今，金宝博的每一步成长都离不开用户的信任与支持
          </p>
        </div>
        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
          <div className="flex flex-col gap-8">
            {brandInfo.milestones.map((milestone, index) => (
              <div
                key={index}
                className={`relative flex items-start gap-6 md:gap-10 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full -translate-x-1/2 mt-1.5 z-10"></div>
                <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-14 md:text-right' : 'md:pl-14'}`}>
                  <div className="bg-background-50 rounded-lg p-5 md:p-6">
                    <span className="inline-block text-xs font-bold text-primary-500 mb-1">
                      {milestone.year}
                    </span>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      {milestone.event}
                    </p>
                  </div>
                </div>
                {index % 2 === 0 ? <div className="hidden md:block md:w-1/2"></div> : <div className="hidden md:block md:w-1/2"></div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="w-full bg-background-50 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
            用户口碑
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            用户怎么说
          </h2>
          <p className="text-sm text-foreground-500 max-w-xl mx-auto">
            数百万用户的真实评价，是我们前进的最大动力
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {brandInfo.testimonials.map((testimonial, index) => (
            <div key={index} className="bg-background-100 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <span className="text-sm font-semibold text-accent-600">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-900">
                    {testimonial.name}
                  </div>
                  <div className="text-xs text-foreground-400">{testimonial.role}</div>
                </div>
              </div>
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`text-xs ${i < testimonial.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                  ></i>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {testimonial.content}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactInfo() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;

    if (honeypot?.trim()) {
      setFormStatus('success');
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'company_alt' && value) {
        body.append(key, value.toString());
      }
    });

    try {
      const res = await fetch('https://form.readdy.ai/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
            联系我们
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            联系方式
          </h2>
          <p className="text-sm text-foreground-500 max-w-xl mx-auto">
            我们随时为您提供帮助与支持
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
          <div className="flex flex-col gap-5">
            <div className="bg-background-50 rounded-lg p-6 flex items-start gap-4">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-phone-line text-lg text-primary-500"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h4>
                <p className="text-sm text-foreground-600">{brandInfo.contact.phone}</p>
                <p className="text-xs text-foreground-400 mt-0.5">{brandInfo.contact.workingHours}</p>
              </div>
            </div>
            <div className="bg-background-50 rounded-lg p-6 flex items-start gap-4">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                <i className="ri-mail-line text-lg text-accent-500"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                <p className="text-sm text-foreground-600">{brandInfo.contact.email}</p>
              </div>
            </div>
            <div className="bg-background-50 rounded-lg p-6 flex items-start gap-4">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100">
                <i className="ri-wechat-line text-lg text-secondary-500"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">官方微信</h4>
                <p className="text-sm text-foreground-600">{brandInfo.contact.wechat}</p>
              </div>
            </div>
            <div className="bg-background-50 rounded-lg p-6 flex items-start gap-4">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-50">
                <i className="ri-alarm-warning-line text-lg text-primary-500"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">24小时紧急热线</h4>
                <p className="text-sm text-foreground-600">{brandInfo.contact.hotline24h}</p>
              </div>
            </div>
          </div>

          <div className="bg-background-50 rounded-lg p-6 md:p-8">
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-5">
              在线留言
            </h3>

            {formStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-emerald-100">
                  <i className="ri-check-line text-2xl text-emerald-500"></i>
                </div>
                <h4 className="text-base font-semibold text-foreground-900 mb-2">提交成功</h4>
                <p className="text-sm text-foreground-500">感谢您的留言，我们会在24小时内与您联系。</p>
                <button
                  onClick={() => { setFormStatus('idle'); setFormError(''); }}
                  className="mt-5 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
                >
                  继续留言
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-readdy-form="">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    手机号码
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的手机号码"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>
                <div className="contact-form-honeypot">
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500">{formError}</p>
                )}
                <button
                  type="submit"
                  className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  提交留言
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="w-full bg-background-50 py-14 md:py-20">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
            帮助中心
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            常见问题
          </h2>
          <p className="text-sm text-foreground-500">
            快速解答您的疑问
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-background-100 rounded-lg overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="text-sm font-medium text-foreground-900 pr-4">
                  {faq.question}
                </span>
                <i
                  className={`text-sm text-foreground-400 transition-transform flex-shrink-0 ${
                    openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                  }`}
                ></i>
              </button>
              {openIndex === index && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function About() {
  const { t } = useTranslation();

  const breadcrumbItems = [
    { label: t('breadcrumb.home'), to: '/' },
    { label: t('breadcrumb.about') },
  ];

  const schemaData = {
    Organization: {
      name: '金宝博',
      url: 'https://www.jinbaobo.com/',
      description: brandInfo.description,
      foundingDate: '2015',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-888-8888',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
        areaServed: 'CN',
      },
    },
    WebPage: {
      name: '关于金宝博 | 品牌介绍、口碑与联系方式',
      description: '全面了解金宝博品牌故事、核心价值、发展历程与用户口碑。金宝博是行业领先的一站式服务平台，服务全球500万+用户，满意度高达98.5%。',
      url: 'https://www.jinbaobo.com/about',
    },
    BreadcrumbList: {
      itemListElement: breadcrumbItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        item: item.to ? `https://www.jinbaobo.com${item.to}` : undefined,
      })),
    },
    FAQPage: {
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" data={schemaData.Organization} />
      <SchemaOrg type="WebPage" data={schemaData.WebPage} />
      <SchemaOrg type="BreadcrumbList" data={schemaData.BreadcrumbList} />
      <SchemaOrg type="FAQPage" data={schemaData.FAQPage} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="mt-4">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                关于金宝博
              </h1>
              <p className="text-sm text-foreground-500">
                了解金宝博的品牌故事、核心价值与发展历程
              </p>
            </div>
          </div>

          <BrandIntro />
          <MissionVision />
          <CoreValues />
          <Milestones />
          <Testimonials />
          <ContactInfo />
          <AboutFAQ />
        </main>
        <Footer />
      </div>
    </>
  );
}

export { BrandIntro, MissionVision, CoreValues, Milestones, Testimonials, ContactInfo, AboutFAQ };